import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { AlertTriangle, CreditCard, Info, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/components/language-provider";
import { getApiUrl } from "@/lib/config";

interface BillingState {
  id: string;
  plan: string;
  status: string;
  paymentFailedAt?: string | null;
  endedAt?: string | null;
  endReason?: string | null;
}

const planName = (plan: string) => (plan === "enterprise" || plan === "customenterprise" ? "Enterprise" : plan === "pro" ? "Pro" : "");

const TEXTS = {
  pt: {
    pastDueTitle: "O pagamento do seu plano não passou",
    pastDueBody: "A Stripe vai tentar cobrar de novo nos próximos dias e, enquanto isso, o plano continua funcionando. Atualize o pagamento para não voltar ao plano Free.",
    update: "Atualizar pagamento",
    endedTitle: (plan: string) => `Seu plano ${plan || "pago"} terminou porque o pagamento não foi feito`,
    endedBody: "Nada foi apagado: documentos, modelos e equipe continuam. Agora valem os limites do plano Free. Para voltar, é só assinar de novo.",
    plans: "Ver planos",
    close: "Fechar aviso",
    failed: "Não deu para abrir a página de pagamento agora. Tente de novo em alguns minutos ou fale com a gente em Ajuda.",
  },
  en: {
    pastDueTitle: "Your plan payment did not go through",
    pastDueBody: "Stripe will try again over the next days and, meanwhile, the plan keeps working. Update the payment to stay off the Free plan.",
    update: "Update payment",
    endedTitle: (plan: string) => `Your ${plan || "paid"} plan ended because it was not paid`,
    endedBody: "Nothing was deleted: documents, templates and team stay. The Free plan limits apply now. To get it back, just subscribe again.",
    plans: "See plans",
    close: "Close notice",
    failed: "The payment page could not open now. Try again in a few minutes or reach us in Help.",
  },
};

const SHOW_ENDED_FOR_MS = 30 * 24 * 60 * 60 * 1000;
const dismissKey = (s: BillingState) => `fs.billing-notice.${s.id}.${s.endedAt}`;
const wasDismissed = (s: BillingState) => {
  try { return localStorage.getItem(dismissKey(s)) === "1"; } catch { return false; }
};

/**
 * Top of every app page, for the person who pays: the renewal payment failed (the plan still works while Stripe retries),
 * or the plan went back to free because it was not paid. Nothing else changes for them.
 */
export function BillingNotice() {
  const { language } = useLanguage();
  const tx = TEXTS[language === "en" ? "en" : "pt"];
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [closed, setClosed] = useState(false);
  const { data } = useQuery<BillingState>({ queryKey: ["/api/v1/subscriptions/current"], staleTime: 5 * 60 * 1000, retry: false });

  if (!data || data.id === "free") return null;

  const pastDue = data.status === "pastdue";
  const endedUnpaid = data.status === "cancelled" && data.endReason === "payment_failed" && !!data.endedAt
    && Date.now() - new Date(data.endedAt).getTime() < SHOW_ENDED_FOR_MS && !closed && !wasDismissed(data);
  if (!pastDue && !endedUnpaid) return null;

  // Stripe's billing page (card, invoices); without the customer portal, the open invoice's payment page.
  const openPayment = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getApiUrl("/api/v1/subscriptions/portal"), { method: "POST", headers: token ? { Authorization: `Bearer ${token}` } : {} });
      const json = await res.json().catch(() => null);
      const url: string | undefined = json?.data?.url ?? json?.url;
      if (!res.ok || !url) throw new Error("no url");
      window.location.href = url;
    } catch {
      toast({ title: tx.failed, variant: "destructive" });
      setBusy(false);
    }
  };

  if (pastDue) {
    return (
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/50 bg-amber-500/10 p-3 sm:p-4" role="alert" data-testid="billing-notice-past-due">
        <div className="flex min-w-0 items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div className="min-w-0 space-y-1">
            <p className="font-medium">{tx.pastDueTitle}</p>
            <p className="text-sm text-muted-foreground">{tx.pastDueBody}</p>
          </div>
        </div>
        <Button size="sm" disabled={busy} onClick={() => void openPayment()} data-testid="button-update-payment">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}{tx.update}
        </Button>
      </div>
    );
  }

  const close = () => {
    try { localStorage.setItem(dismissKey(data), "1"); } catch { /* private window: closes for this visit only */ }
    setClosed(true);
  };
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-sky-500/40 bg-sky-500/10 p-3 sm:p-4" role="status" data-testid="billing-notice-ended">
      <div className="flex min-w-0 items-start gap-3">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-sky-600" />
        <div className="min-w-0 space-y-1">
          <p className="font-medium">{tx.endedTitle(planName(data.plan))}</p>
          <p className="text-sm text-muted-foreground">{tx.endedBody}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" asChild data-testid="button-see-plans">
          <Link href="/settings?action=plans">{tx.plans}</Link>
        </Button>
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={close} aria-label={tx.close} data-testid="button-dismiss-billing-notice">
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
